// Pairing helpers for the addendum tenant repeater. Kept pure so they can be
// unit-tested and re-run safely after the manager has renamed a row.

const normName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** ", "-joined names — the string printed as "Tenant(s)" on the document. */
export const deriveTenantNames = (rows) =>
  (rows || [])
    .map((r) => String(r?.name || "").trim())
    .filter(Boolean)
    .join(", ");

/**
 * Fill blank emails on `rows` from `leaseTenants`, matching by normalised name
 * first and falling back to positional order for whatever is left. A lease
 * tenant is consumed by at most one row. Rows that already carry an email and
 * rows with no match are returned untouched.
 */
export const pairEmailsFromLease = (rows, leaseTenants) => {
  const source = (leaseTenants || []).filter((t) => t && t.email);
  if (!source.length) return (rows || []).map((r) => ({ ...r }));

  const used = new Set();
  const out = (rows || []).map((r) => ({ ...r }));

  out.forEach((row) => {
    if (String(row.email || "").trim()) return;
    const wanted = normName(row.name);
    if (!wanted) return;
    const hit = source.findIndex((t, i) => !used.has(i) && normName(t.name) === wanted);
    if (hit !== -1) {
      used.add(hit);
      row.email = source[hit].email;
    }
  });

  // Positional fallback for rows still blank, in order, skipping consumed ones.
  out.forEach((row) => {
    if (String(row.email || "").trim()) return;
    const next = source.findIndex((_, i) => !used.has(i));
    if (next !== -1) {
      used.add(next);
      row.email = source[next].email;
    }
  });

  return out;
};

/**
 * Seed repeater rows for a brand-new addendum. Prefers the unit's tenant list,
 * then the mastersheet's joined name string, then a single empty row so the
 * manager always has something to type into.
 */
export const seedTenantRows = (item = {}, tenants = []) => {
  const fromList = (tenants || [])
    .map((t) => ({
      name:
        t.name ||
        `${t.first_name || t.firstName || ""} ${t.last_name || t.lastName || ""}`.trim(),
      email: t.email || "",
    }))
    .filter((r) => r.name || r.email);
  if (fromList.length) return fromList;

  const joined =
    (typeof item.tenants === "string" && item.tenants.trim()) ||
    (typeof item.tenant === "string" && item.tenant.trim()) ||
    "";
  if (joined) {
    return joined
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean)
      .map((name) => ({ name, email: "" }));
  }
  return [{ name: "", email: "" }];
};

/** Rehydrate repeater rows when editing an existing addendum. */
export const rowsFromExisting = (existing = {}) => {
  const sigs = existing.tenant_signatures || [];
  if (sigs.length) {
    return sigs.map((s) => ({
      name: s.name || "",
      email: s.email || "",
      signed: !!s.signed,
      signature: s.signature || "",
      date: s.date || "",
    }));
  }
  return String(existing.tenant_names || "")
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean)
    .map((name) => ({ name, email: "" }));
};
